import React from 'react';

import cx from 'classnames';
import { Control, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { BaseRadioButtonGroup } from 'components/shared/BaseFormComponents/RadioButton/RadioButtonGroup';
import { BaseSelect } from 'components/shared/BaseFormComponents/Select';
import Divider from 'components/shared/Divider';
import { FormAccordion, FormAccordionItem } from 'components/shared/FormAccordion';
import { HookCheckbox, HookTimeInput } from 'components/shared/HookFormComponents';
import { HookInput } from 'components/shared/HookFormComponents/Input';
import { HookWysiwyg } from 'components/shared/HookFormComponents/Wysiwyg';
import Visibility from 'components/shared/Icon/icons/Visibility';
import Text from 'components/shared/Typography/Text';
import { activityEventTypeToName } from 'detailsv2/structure';
import { canCreateNewContentPageSelector } from 'store/user/selectors';

import cs from '../../../../index.module.css';
import { ChangePagePopover } from '../../../../shared';
import IconPicker from '../../../../shared/IconPicker';
import { DayOrDatePicker } from '../../DayOrDatePicker';
import s from './index.module.css';
import * as types from './types';
import { useActivityFormContext } from './useActivityForm';

function ActivityForm({
  tripEvent,
  loadOptions,
  openPagePreview,
  isVisibleFormBody,
  onCreateOption,
  isMultiDayActivity,
  onChangePage,
  popover,
}: types.FormProps) {
  const { control } = useActivityFormContext();
  const isCreateDisabled = useSelector(canCreateNewContentPageSelector);

  // TODO: rework control props for adapters & delete it
  const depricatedControl = control as Control<any>;

  const radioButtons = React.useMemo(() => {
    const array = [];
    for (let key in activityEventTypeToName) {
      // TODO: can be removed after upgrade TS version
      const typedKey = key as keyof typeof activityEventTypeToName;
      array.push({ name: activityEventTypeToName[typedKey], id: key });
    }
    return array;
  }, []);

  return (
    <>
      <FormAccordion>
        <FormAccordionItem header="Required Information" isRequired>
          <div className={cs.row}>
            <div>
              <Text isStrong size="regular">
                Content Page
              </Text>
              <Text color="gray-600" size="medium">
                Search all page types except
                <br />
                Property pages
              </Text>
            </div>

            <div>
              <Controller
                name="attachedPage"
                control={control}
                render={({ field: { value }, fieldState: { error } }) => {
                  return (
                    <div ref={popover.ref}>
                      <BaseSelect
                        placeholder="Search Content Library"
                        isClearable
                        defaultOption={value}
                        loadOptions={loadOptions}
                        optionActionIcon={Visibility}
                        onOptionActionClick={openPagePreview}
                        onChange={onChangePage}
                        errorMessage={error?.message}
                        isCreatable
                        isCreateDisabled={isCreateDisabled}
                        onOptionCreation={onCreateOption}
                      />
                    </div>
                  );
                }}
              />

              {popover.isOpen && (
                <ChangePagePopover
                  anchorEl={popover.ref.current}
                  onClose={popover.onClose}
                  onKeep={popover.onKeep}
                  onReset={popover.onReset}
                />
              )}

              <div className={cs.collapseCheckbox}>
                <HookCheckbox name="data.isCollapsible" control={depricatedControl} className={cs.checkboxText}>
                  <Text color="gray-600" size="medium">
                    Collapse content within More Details
                  </Text>
                </HookCheckbox>
              </div>
            </div>
          </div>

          <Divider />

          <div className={cs.row}>
            <div>
              <Text isStrong size="regular">
                Icons
              </Text>
              <Text color="gray-600" size="medium">
                Optionally customize your icon,
                <br /> because we’re awesome.
              </Text>
            </div>
            <div>
              <IconPicker
                name="icon"
                control={depricatedControl}
                eventType={tripEvent.type}
                isImprovedUIOnEventModalsFeature
              />
            </div>
          </div>
        </FormAccordionItem>

        {isVisibleFormBody && (
          <>
            <FormAccordionItem header="Times">
              <div>
                <Controller
                  name="data.type"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <BaseRadioButtonGroup value={value} onChange={onChange} options={radioButtons} />
                  )}
                />
              </div>

              <div className={cx(s.daysOrDatesContainer)}>
                <DayOrDatePicker isMultiDayActivity={isMultiDayActivity} />
              </div>
              {!isMultiDayActivity && (
                <div className={cx(cs.inputGroup)}>
                  <HookTimeInput control={depricatedControl} name="data.time.start" label="Start Time" />
                  <HookTimeInput control={depricatedControl} name="data.time.end" label="End Time" />
                </div>
              )}
            </FormAccordionItem>
            <FormAccordionItem header="Event Title">
              <HookInput control={depricatedControl} name="data.title" label="Title" />
            </FormAccordionItem>
            <FormAccordionItem header="Details">
              <div className={cs.inputGroup}>
                <HookInput label="Confirmation Number" control={depricatedControl} name="data.confirmationNumber" />
                <HookInput label="Contact Information" control={depricatedControl} name="data.contactInfo" />
                <HookInput label="Website" control={depricatedControl} name="data.website" />
                <HookInput label="Location" control={depricatedControl} name="data.location" />
              </div>
              <div>
                <div className={cs.details}>Add Optional Details</div>
                <div>
                  <HookWysiwyg
                    name="data.details"
                    control={depricatedControl}
                    placeholder="Enjoy a lovely catered picnic lunch!"
                  />
                </div>
              </div>
            </FormAccordionItem>
            <FormAccordionItem header="Notes">
              <HookWysiwyg
                name="data.notes"
                control={depricatedControl}
                placeholder="Add any important notes to be shown on the Travel Brief and on the day page..."
              />
            </FormAccordionItem>

            <div className={cs.marginBottom} />
          </>
        )}
      </FormAccordion>
    </>
  );
}

export { ActivityForm };
